import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, debounceTime, distinctUntilChanged, map, of, startWith, Subject, Subscription, switchMap } from 'rxjs';

import {
  ChannelStats,
  DisplayProjections,
  LogStatsResponse,
} from 'src/app/model/response/log-display';
import { FileChannelsError, FileChannelsService, FtFileType } from 'src/app/service/file-channels.service';
import { classifyStatCell, preferByChannel } from 'src/app/shared/log-display.util';
import { formatTruncated } from 'src/app/shared/number-format.util';

/** Canal da legenda do gráfico principal; `name` é o nome EXATO do servidor (D6). */
export interface StatsPanelChannel {
  name: string;
  label: string;
  color: string;
}

/** Os três estados de um canal na tabela (D8): com estatística, sem dado válido e ausente do log. */
type RowState = 'stats' | 'missing';

export interface StatsRow {
  name: string;
  label: string;
  color: string;
  state: RowState;
  /** Unidade da entrada ESCOLHIDA (canônica ou projetada); null = desconhecida. */
  unit: string | null;
  /** Entrada vinda de `display.stats` (projeção só de tela). */
  projected: boolean;
  stats: ChannelStats | null;
}

/** Colunas numéricas da tabela, na ordem exibida. */
export const STAT_COLUMNS = ['min', 'max', 'mean', 'std', 'p5', 'p95'] as const;
export type StatColumn = (typeof STAT_COLUMNS)[number];

/** Formato do log: ponto decimal, sem agrupamento, no máximo 3 casas cortadas (D1). */
const NUMBER_FORMAT = { maxDecimals: 3, locale: 'en-US', useGrouping: false } as const;

/** Espera para juntar mudanças seguidas de canais/projeções em um único pedido. */
const REQUEST_DEBOUNCE_MS = 150;

interface StatsOutcome {
  response: LogStatsResponse | null;
  error: FileChannelsError | null;
  /** Canais do pedido: a tabela segue o que foi pedido, não o que a tela já mudou. */
  channels: StatsPanelChannel[];
}

/**
 * Estatísticas do log inteiro por canal (contrato 2 §12). Não plota: o gráfico principal
 * usa o Arrow. Cada canal resolve entre o bloco canônico e o projetado (§12.3), nunca
 * `display ?? response`, e a unidade mostrada é a da entrada escolhida.
 */
@Component({
  selector: 'app-log-stats-panel',
  standalone: true,
  imports: [CommonModule, TranslocoModule, TooltipModule],
  templateUrl: './log-stats-panel.component.html',
  styleUrl: './log-stats-panel.component.css',
})
export class LogStatsPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userID = '';
  @Input() fileID = '';
  @Input() fileType: FtFileType = 'ftlog';
  @Input() channels: StatsPanelChannel[] = [];
  /** Montado pelo pai com `buildDisplayProjections` a partir do toggle bar/psi. */
  @Input() projections: DisplayProjections | null = null;

  readonly columns = STAT_COLUMNS;

  open = true;
  loading = false;
  error: FileChannelsError | null = null;
  response: LogStatsResponse | null = null;
  rows: StatsRow[] = [];

  /** Incrementa no "tentar de novo" para furar o distinctUntilChanged. */
  private attempt = 0;
  private readonly trigger$ = new Subject<void>();
  private sub?: Subscription;

  constructor(private readonly channelsService: FileChannelsService) {}

  ngOnInit(): void {
    this.sub = this.trigger$
      .pipe(
        debounceTime(REQUEST_DEBOUNCE_MS),
        map(() => this.snapshot()),
        distinctUntilChanged((a, b) => a.key === b.key),
        // switchMap cancela o pedido anterior: resposta velha nunca chega à tela
        switchMap(({ channels, projections }) => this.fetch(channels, projections)),
      )
      .subscribe((outcome) => this.apply(outcome));
    this.trigger$.next();
  }

  ngOnChanges(): void {
    // Antes do ngOnInit o pipeline ainda não existe: o ngOnInit dispara o primeiro pedido.
    if (this.sub) this.trigger$.next();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  toggle(): void {
    this.open = !this.open;
  }

  retry(): void {
    this.attempt++;
    this.trigger$.next();
  }

  /** Texto de um valor estatístico: null → "—", 0 → "0", ausente → vazio (D8). */
  cell(value: number | null | undefined): string {
    switch (classifyStatCell(value)) {
      case 'value':
        return formatTruncated(value as number, NUMBER_FORMAT);
      case 'null':
        return '—';
      default:
        return '';
    }
  }

  cellState(value: number | null | undefined): string {
    return classifyStatCell(value);
  }

  /** Duração em segundos, cortada (D3) e com ponto decimal. */
  get durationSeconds(): string {
    return formatTruncated((this.response?.period.duration_ms ?? 0) / 1000, NUMBER_FORMAT);
  }

  trackRow(_: number, row: StatsRow): string {
    return row.name;
  }

  private snapshot(): { key: string; channels: StatsPanelChannel[]; projections: DisplayProjections | null } {
    const channels = [...this.channels];
    const projections = this.projections;
    const key = JSON.stringify([
      this.userID,
      this.fileID,
      this.fileType,
      channels.map((c) => c.name).sort(),
      projections,
      this.attempt,
    ]);
    return { key, channels, projections };
  }

  private fetch(channels: StatsPanelChannel[], projections: DisplayProjections | null) {
    if (!this.userID || !this.fileID || channels.length === 0) {
      return of<StatsOutcome>({ response: null, error: null, channels });
    }
    // O gráfico principal usa Arrow: aqui só os números (sem amostras nem buckets) e o log inteiro.
    return this.channelsService
      .stats(this.userID, this.fileID, this.fileType, {
        channels: channels.map((c) => c.name),
        include_samples: false,
        include_aggregates: false,
        ...(projections ? { display_projections: projections } : {}),
      })
      .pipe(
        map((response): StatsOutcome => ({ response, error: null, channels })),
        catchError((error: FileChannelsError) => of<StatsOutcome>({ response: null, error, channels })),
        startWith('loading' as const),
      );
  }

  private apply(outcome: StatsOutcome | 'loading'): void {
    if (outcome === 'loading') {
      this.loading = true;
      return;
    }
    this.loading = false;
    this.error = outcome.error;
    this.response = outcome.response;
    this.rows = outcome.response ? this.buildRows(outcome.channels, outcome.response) : [];
  }

  private buildRows(channels: StatsPanelChannel[], response: LogStatsResponse): StatsRow[] {
    // §12.3: cada canal resolve sozinho entre canônico e projetado.
    const chosen = new Map(
      preferByChannel(response.stats, response.display?.stats).map((item) => [item.channel, item]),
    );
    const projected = new Set((response.display?.stats ?? []).map((item) => item.channel));
    const missing = new Set(response.missing_channels);

    return channels.map((channel): StatsRow => {
      const stats = chosen.get(channel.name) ?? null;
      const absent = missing.has(channel.name) || !stats;
      return {
        ...channel,
        state: absent ? 'missing' : 'stats',
        unit: stats?.unit ?? null,
        projected: !absent && projected.has(channel.name),
        stats: absent ? null : stats,
      };
    });
  }
}
